import React from "react";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import BlockRenderer from "../components/BlockRenderer";
import ContactForm from "../components/ContactForm";
import { CrumbLine, HeroBand, SectionHead, SiblingPills, useEnquiry } from "./parts";

function DetailRow({ icon: Icon, label, value, href }) {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;

  // Trading hours arrive from settings as [{ days, time }] - render as rows.
  const body = Array.isArray(value) ? (
    <dl className="space-y-1" data-testid="contact-hours-list">
      {value.map((h, i) => (
        <div key={i} className="flex flex-wrap gap-x-2 text-sm">
          <dt className="text-[color:var(--nq-stone)]">{h.days}</dt>
          <dd className="font-medium text-[color:var(--nq-sand)]">{h.time}</dd>
        </div>
      ))}
    </dl>
  ) : href ? (
    <a
      href={href}
      className="text-sm font-medium text-[color:var(--nq-sand)] transition-colors duration-200 hover:text-[color:var(--nq-gold)]"
      data-testid={`contact-${label.toLowerCase()}-link`}
    >
      {value}
    </a>
  ) : (
    <span className="text-sm font-medium text-[color:var(--nq-sand)]">{value}</span>
  );
  return (
    <div className="flex gap-3">
      <span
        className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
        style={{ background: "rgba(31,107,58,0.24)" }}
      >
        <Icon className="h-4 w-4 gold-text" />
      </span>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-[color:var(--nq-stone)]">{label}</p>
        <div className="mt-0.5">{body}</div>
      </div>
    </div>
  );
}

/**
 * CONTACT page - split layout: the migrated enquiry form on the left,
 * the real contact details from site settings on the right.
 */
export default function ContactTemplate({
  page,
  siblings = [],
  breadcrumbs = [],
  parentLabel,
  contact = {},
}) {
  const heroImg = page.hero_image || page.thumb_image;
  const to = useEnquiry(page, heroImg, parentLabel);
  const heading = page.h1 && page.h1.length < 90 ? page.h1 : page.title;

  // The migrated contact page is mostly heading-only widgets ("Our Location",
  // "Connect with NQ Springs") with no body copy. Stacked bare headings read as
  // broken, so keep only blocks that actually carry content.
  const blocks = (page.blocks || []).filter((b) =>
    ["paragraph", "list", "table", "quote"].includes(b.type)
  );

  return (
    <div data-testid="template-contact">
      <HeroBand image={heroImg} dim={0.18}>
        <CrumbLine breadcrumbs={breadcrumbs} parentLabel={parentLabel} />
        <div className="mt-2 max-w-2xl">
          <h1
            className="text-3xl font-bold leading-tight text-[color:var(--nq-sand)] md:text-5xl"
            data-testid="page-title"
          >
            {heading}
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-[color:var(--nq-stone)] md:text-base">
            {page.meta_description ||
              "Send us your vehicle details and what you're after - we'll come back with options, pricing and fitment."}
          </p>
        </div>
      </HeroBand>

      <div className="container-nq section-nq">
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7" id="enquiry-form">
            <SectionHead eyebrow="Send an enquiry" title="Tell us what you need" />
            <ContactForm page={page} />
          </div>

          <aside className="lg:col-span-5">
            <div className="card-nq p-6" data-testid="contact-details-card">
              <p className="eyebrow">Get in touch</p>
              <h2 className="mt-2 text-lg font-semibold text-[color:var(--nq-sand)]">
                Workshop details
              </h2>
              <div className="mt-5 space-y-5">
                <DetailRow
                  icon={Phone}
                  label="Phone"
                  value={contact.phone}
                  href={contact.phone ? `tel:${String(contact.phone).replace(/[^\d+]/g, "")}` : null}
                />
                <DetailRow
                  icon={Mail}
                  label="Email"
                  value={contact.email}
                  href={contact.email ? `mailto:${contact.email}` : null}
                />
                <DetailRow icon={MapPin} label="Address" value={contact.address} />
                <DetailRow icon={Clock} label="Hours" value={contact.hours} />
              </div>
            </div>

            {blocks.length > 0 && (
              <div className="card-nq mt-6 p-6">
                <BlockRenderer blocks={blocks} />
              </div>
            )}
          </aside>
        </div>

        <div className="mt-14">
          <SiblingPills siblings={siblings} />
        </div>
        <span className="hidden" data-enquire-href={to} />
      </div>
    </div>
  );
}
